/**
 * Shared "smart-capture" extraction helper.
 *
 * Used by both the `SmartCaptureBlock` (the explicit From-Photo button)
 * and the `PhotoCaptureField` (the Take-Photo attachment workflow) so a
 * user who taps EITHER camera CTA gets:
 *   1. A local ZXing pass for a fast barcode hit.
 *   2. A POST to `/api/product-requests/extract` for product_name + brand.
 *   3. Field updates via `onApply({barcode?, product_name?, brand?, source})`.
 *   4. A toast that accurately matches the outcome — no silent successes,
 *      no green "Detected" when fields are blank.
 *
 * `extractProductFromImage` ALWAYS resolves with a small status object so
 * the caller never needs to wrap it in another try/catch:
 *
 *   { appliedAny: boolean, appliedFields: { ...present-only... }, error?: string }
 */
import { BrowserMultiFormatReader } from "@zxing/browser";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./api";

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error || new Error("file read failed"));
    fr.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}

export async function resizeFileToJpegDataUrl(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);
  let { width, height } = img;
  if (width <= 0 || height <= 0) throw new Error("invalid image");
  const longest = Math.max(width, height);
  if (longest > MAX_EDGE) {
    const s = MAX_EDGE / longest;
    width = Math.round(width * s);
    height = Math.round(height * s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return { dataUrl: canvas.toDataURL("image/jpeg", JPEG_QUALITY), width, height };
}

async function tryZxingDecode(dataUrl) {
  try {
    const img = await loadImage(dataUrl);
    const reader = new BrowserMultiFormatReader();
    const res = await reader.decodeFromImageElement(img);
    const raw = (res && res.getText && res.getText()) || null;
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, "");
    return digits || raw.trim() || null;
  } catch {
    return null;
  }
}

/**
 * Run smart-capture extraction on an in-memory JPEG data URL.
 *
 * @param {string} dataUrl - JPEG data URL ≤1.5 MB.
 * @param {(applied: object) => void} onApply - Called with `{barcode?, product_name?, brand?, source}` when at least one field landed.
 * @param {object} opts
 * @param {boolean} [opts.suppressToasts=false] - When true, no toasts are fired. Used when the caller wants to chain its own messaging.
 * @returns {Promise<{appliedAny:boolean, appliedFields:object, error?:string, source?:string, summary?:string}>}
 */
export async function extractProductFromImage(dataUrl, onApply, opts = {}) {
  const suppressToasts = !!opts.suppressToasts;
  /* eslint-disable no-console */
  if (!dataUrl) return { appliedAny: false, appliedFields: {}, error: "no_image" };
  console.debug("[smart-capture] extract begin", { dataUrlChars: dataUrl.length });

  const applied = { barcode: undefined, product_name: undefined, brand: undefined };
  let source = null;
  let serverError = null;
  let httpError = null;

  // 1) Local ZXing pass
  const localBarcode = await tryZxingDecode(dataUrl);
  console.debug("[smart-capture] zxing-still result", { localBarcode });
  if (localBarcode) {
    applied.barcode = localBarcode;
    source = "barcode_scan";
  }

  // 2) Vision extraction
  try {
    console.debug("[smart-capture] extract POST sent", {
      mode: localBarcode ? "label" : "auto",
      payloadKb: Math.round(dataUrl.length / 1024),
    });
    const { data } = await api.post(
      "/product-requests/extract",
      { image: dataUrl, mode: localBarcode ? "label" : "auto" },
      { timeout: 90_000 },
    );
    console.debug("[smart-capture] extract response", data);
    if (data && data.ok) {
      if (!applied.barcode && data.barcode) applied.barcode = data.barcode;
      if (data.product_name) applied.product_name = data.product_name;
      if (data.brand) applied.brand = data.brand;
    } else {
      serverError = (data && data.error) || "unknown_error";
      console.warn("[smart-capture] extract returned ok=false", data);
    }
  } catch (apiErr) {
    httpError = formatApiErrorDetail(apiErr) || "request failed";
    console.warn("[smart-capture] extract HTTP failed", httpError);
  }
  /* eslint-enable no-console */

  const gotName = !!applied.product_name;
  const gotBrand = !!applied.brand;
  const gotBarcode = !!applied.barcode;
  const gotLabelData = gotName || gotBrand;

  // Nothing useful at all — clean error.
  if (!gotBarcode && !gotLabelData) {
    let msg = "Couldn't read this photo — fill the fields manually.";
    if (serverError === "extraction_not_configured") msg = "Photo OCR is not configured on this server. Please type the fields.";
    else if (serverError === "image_too_large") msg = "Photo is too large — try a smaller version.";
    else if (serverError === "llm_call_failed") msg = "Photo OCR is temporarily unavailable. Please type the fields.";
    else if (serverError === "missing_or_bad_image" || serverError === "invalid_base64") msg = "Photo couldn't be read — please try a different one.";
    else if (httpError) msg = `Photo OCR couldn't run (${httpError}). Please type the fields.`;
    if (!suppressToasts) toast.error(msg);
    return { appliedAny: false, appliedFields: {}, error: serverError || (httpError ? "http_error" : "no_data") };
  }

  // Apply the fields we did land
  const appliedFields = {};
  if (gotBarcode) appliedFields.barcode = applied.barcode;
  if (gotName) appliedFields.product_name = applied.product_name;
  if (gotBrand) appliedFields.brand = applied.brand;
  const finalSource = source || "photo_ocr";
  if (typeof onApply === "function") {
    try { onApply({ ...appliedFields, source: finalSource }); } catch { /* parent guard */ }
  }

  // Honest, scenario-specific toast
  let summary = null;
  if (!suppressToasts) {
    if (gotName) {
      const bits = [applied.product_name];
      if (gotBrand) bits.push(`(${applied.brand})`);
      summary = bits.join(" ");
      if (gotBrand) toast.success("Detected — review and edit below");
      else toast.warning("Read the product name. Brand not detected — please add it if known.");
    } else if (gotBrand) {
      summary = `Brand: ${applied.brand}`;
      toast.warning("Detected the brand only — please type the product name.");
    } else if (gotBarcode) {
      summary = `Barcode ${applied.barcode} — product name not detected`;
      toast.warning("Read the barcode but couldn't read the product name. Please type the name and brand.");
    }
  }

  return { appliedAny: true, appliedFields, source: finalSource, summary };
}
